<template>
    <tr :data-id="discountDetails?._id">
        <td class="discount-name">{{ discountDetails?.discountName }}</td>
        <td>
            <div @click="isFullDescriptionShown = !isFullDescriptionShown" class="description">
                <span v-if="!isFullDescriptionShown && discountDetails?.description.length > 70">
                    {{ truncatedDescription }}
                    <v-icon class="ml-1" size="20" color="#212121">
                        mdi-menu-down
                    </v-icon>
                </span>
                <span v-else>{{ discountDetails?.description }}</span>
            </div>
        </td>
        <td>{{ translateDiscountType }}</td>
        <td>
            <div v-if="discountDetails?.discountType !== 'fixprice'" class="font-weight-bolder">
                {{ discountDetails?.discountValue }}
            </div>
            <FixedPriceCellContent
                v-else
                v-for="price in formatFixPriceValues('fixPrice')"
                :key="price.label"
                :price="price"
                :discount="discountDetails"
                class="v-application d-flex"
            />
        </td>
        <td>{{ translateDiscountTypeFlatRate }}</td>
        <td>
            <div v-if="discountDetails?.flatRateDiscountType !== 'fixprice'" class="font-weight-bolder">
                {{ discountDetails?.flatRateDiscountValue }}
            </div>
            <FixedPriceCellContent
                v-else
                v-for="price in formatFixPriceValues('flatRateFixPrice')"
                :price="price"
                :key="price.label"
                :discount="discountDetails"
                class="v-application d-flex"
            />
        </td>
        <td class="date">{{ discountDetails?.startDate }}</td>
        <td class="date">{{ discountDetails?.endDate }}</td>
        <td>
            <div class="v-application--is-ltr">
                <v-switch
                    class="toggle-discount-btn"
                    v-model="updatedDiscount.isActive"
                    @change="toggleDiscountStatus"
                    dense
                />
            </div>
        </td>
        <td>
            <div class="action-btn-container">
                <v-btn
                    class="btn-link"
                    x-small
                    fab
                    outlined
                    text
                    @click="isLinkedPharmaciesDialogOpen = true">
                    <v-icon small style="margin: 0 8px">
                        mdi-clipboard-account
                    </v-icon>
                </v-btn>
                <v-btn
                    class="btn-edit"
                    x-small
                    fab
                    outlined
                    text
                    @click="isEditDialogOpen = true">
                    <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                    class="btn-delete"
                    x-small
                    fab
                    outlined
                    text
                    color="error"
                    :disabled="shouldDeleteBeDisabled"
                    @click="isDeleteDiscountDialogOpen = true">
                    <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
            </div>
        </td>
        <DiscountFormDialog
            v-model="updatedDiscount"
            dialogTitle="Bearbeiten Rabatt"
            ctaTitle="Rabatt bearbeiten"
            :isDiscountFormDialogOpen.sync="isEditDialogOpen"
            @refresh="$emit('refresh')"
            @submit="editDiscount"
        />
        <LinkedPharmacyDialog
            :discountId="discountDetails._id"
            :subscribedPharmacies="discountDetails.subscribedPharmacies"
            :isLinkedPharmacyDialogOpen.sync="isLinkedPharmaciesDialogOpen"
            @refresh="$emit('refresh')"
            @submit="assignDiscountToPharmacy"
        />
        <DeleteDiscountDialog
            :isDeleteDiscountDialogOpen.sync="isDeleteDiscountDialogOpen"
            @submit="deleteDiscount"
        />
    </tr>
</template>
<script>
import axios from "axios";
import { mapMutations } from "vuex";
import LinkedPharmacyDialog from "./LinkedPharmacyDialog.vue";
import DeleteDiscountDialog from "./DeleteDiscountDialog.vue";
import DiscountFormDialog from "./DiscountFormDialog.vue";
import FixedPriceCellContent from "./FixedPriceCellContent.vue";
import { truncateByWord } from "../../../helpers.js";

export default {
    components: {
        LinkedPharmacyDialog,
        DiscountFormDialog,
        DeleteDiscountDialog,
        FixedPriceCellContent,
    },
    props: {
        discountDetails: Object,
    },
    data() {
        return {
            isLinkedPharmaciesDialogOpen: false,
            isDeleteDiscountDialogOpen: false,
            isEditDialogOpen: false,
            updatedDiscount: {
                ...this.discountDetails,
                subscribedPharmacies: { ...this.discountDetails.subscribedPharmacies }
            },
            isFullDescriptionShown: false,
        };
    },
    computed: {
        shouldDeleteBeDisabled() {
            return Object.keys(this.updatedDiscount.subscribedPharmacies || {}).length !== 0 ;
        },
        translateDiscountType() {
            return this.translateType(this.updatedDiscount?.discountType);
        },
        translateDiscountTypeFlatRate(discountType) {
            return this.translateType(this.updatedDiscount?.flatRateDiscountType);
        },
        truncatedDescription() {
            return truncateByWord(this.updatedDiscount?.description);
        }
    },
    methods:{
        translateType(discountType) {
            if (discountType === "percentage") return "Prozent";
            if (discountType === "number") return "Nummer";
            if (discountType === "fixprice") return "Fixpreis";
            return "";
        },
        formatFixPriceValues(prefix) {
            const fields = [
                {
                    label: "Apotheker",
                    baseRateKey: `${prefix}SubstitutionBaseRate`,
                    marginKey: `${prefix}SubstitutionMargin`,
                },
                {
                    label: "PTA",
                    baseRateKey: `${prefix}PtaBaseRate`,
                    marginKey: `${prefix}PtaMargin`,
                },
                {
                    label: "PKA",
                    baseRateKey: `${prefix}PkaBaseRate`,
                    marginKey: `${prefix}PkaMargin`,
                },
            ];
            return fields
        },
        formatFixPriceValue(value) {
            return Boolean(value) ? value : "-";
        },
        async toggleDiscountStatus(discountStatus){
            try {
                const status = discountStatus ? "active" : "inactive";
                const discountId = this.updatedDiscount?._id;
                if(discountId){
                    const response = await axios.put(`/api/discounts/${discountId}/change_discount_status_to/${status}`);
                    if(response.data.length === 2 && response.data[1] > 300){
                        const fallbackErrorMessage = "Einige Felder fehlen. Bitte gib den Namen, die Art und den Wert des Rabatts ein, um den Rabatt zu erstellen.";
                        this.alertError(response?.data[0]?.error || fallbackErrorMessage);
                        return;
                    }
                    this.alertSuccess(`Der Rabatt wurde ${status === 'active' ? "aktiviert" : "deaktiviert"}`);
                    this.$emit('refresh');
                }
            } catch (error) {
                this.alertError();
            }
        },
        async handleSuccessfulResponse() {
            this.$emit('refresh');
            this.isEditDialogOpen = false;
            this.alertSuccess(`Der Rabatt wurde geändert`);
        },
        async editDiscount(){
            try{
                if(!this.updatedDiscount._id){
                    return;
                }
                const { _id, ...rest} = this.updatedDiscount;
                const response = await axios.put(`/api/discounts/${_id}`, {...rest});
                if(response.status >= 300 || (response?.data?.length === 2 && response?.data[1] > 300)) {
                    const fallbackErrorMessage = "Einige Felder fehlen. Bitte gib den Namen, die Art und den Wert des Rabatts ein, um den Rabatt zu erstellen.";
                    throw new Error(response?.data[0]?.error || fallbackErrorMessage);
                }
                else if (response.status === 200) {
                    await this.handleSuccessfulResponse();
                }
            } catch (error) {
                this.alertError();
            }
        },
        async deleteDiscount() {
            try {
                const discount_id = this.updatedDiscount._id;
                await axios.delete(`/api/discounts/${discount_id}`);
                this.isDeleteDiscountDialogOpen = false;
                this.$emit('refresh');
            } catch (error) {
                this.alertError();
            }
        },
        ...mapMutations({
            alertError: "alert/error",
            alertSuccess: "alert/success",
        }),
    },
};
</script>
<style scoped lang="scss">

.action-btn-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px
}
::v-deep .v-application--wrap {
    min-height: fit-content;
}

td {
    color: rgb(33, 33, 33) !important;
    padding-block: 12px !important;
}

td {
    border: 1px solid rgba(0, 0, 0, 0.12);
    text-align: center;
}
td.discount-name {
    font-weight: 600;
    font-size: 1.4em;
}
td.date {
    white-space: nowrap !important;
}
.font-weight-bolder {
    font-weight: 700;
}
.description {
    cursor: pointer;
}
.description:hover {
    text-decoration: underline;
}
</style>
